import produce from 'immer';
import JWT from 'jsonwebtoken';
import { registerReducer } from '../../../StateSetup/RootReducer';
import { APP_CONSTANTS } from '../../../utils/AppConstants';

const BFF_BASE_URL = window.__ENV__.BFF_URL;
const OK = 200;

// Action names
const ACTION_NAME_PREFIX = 'ACTION_REFRESH_TOKEN';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}_REQUEST`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}_RESPONSE`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}_ERROR`;

/**
 * Refreshes JWT token for a member.
 * @param {object} logger
 */
export const RefreshToken = () => {
    return async (dispatch, getState, { logger }) => {
        const { jwt, isCiamSession } = getState().Authentication;
        const transactionId = logger.generateGuid();
        const IAM_TOKEN_REFRESH_PATH = `${BFF_BASE_URL}/identity/v2/refresh`;
        const CIAM_TOKEN_REFRESH_PATH = `${BFF_BASE_URL}/identity/v3/member/session/refresh`;
        const identityRefreshPath = isCiamSession ? CIAM_TOKEN_REFRESH_PATH : IAM_TOKEN_REFRESH_PATH;

        logger.info(`${ACTION_NAME_PREFIX}: Refresh JWT token for a member.`, { correlation: { transactionId } });

        // Call BFF to with a JWT and get back a new JWT
        const response = await fetch(identityRefreshPath, {
            method: 'POST',
            headers: {
                'gb-authorization': jwt,
            },
        });
        const responseBody = await response.json();

        if (response.status !== OK) {
            const { code, message } = responseBody;

            logger.critical(
                `${ACTION_NAME_PREFIX}: Failure when refreshing JWT token for a member. Code: ${code} Reason: ${message}`,
                {
                    correlation: { transactionId },
                }
            );

            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }

        const newJWT = isCiamSession ? responseBody['gb-authorization'] : responseBody.gbauthorization;
        const decodedNewToken = JWT.decode(newJWT);

        // Calculating clock correction for session expiration management
        const serverTimestamp = responseBody['gb-server-timestamp'];
        sessionStorage.setItem(APP_CONSTANTS.MPROFILE_SESSION_OFFSET, Date.now() - serverTimestamp);

        return dispatch({
            type: RESPONSE_ACTION_NAME,
            jwt: newJWT,
            isCiamSession: Object.hasOwn(decodedNewToken, 'CIAM'),
            isSteppedUp: decodedNewToken?.CIAM?.amr?.includes('otp'),
            success: true,
            group: decodedNewToken?.Identity.group,
        });
    };
};

export const responseReducer = produce((state, action) => {
    state.Authentication.jwt = action.jwt;
    state.Authentication.isCiamSession = action.isCiamSession;
    state.Authentication.isSteppedUp = action.isSteppedUp;
    state.Authentication.group = action.group;
});

/**
 * Fired off when the request encouters an error.
 * @param {object} state
 */
export const errorReducer = produce((state, action) => {
    state.Authentication.hasError = true;
    state.Errors.tokenUpdate.hasError = true;
    state.Errors.tokenUpdate.code = action.code;
    state.Errors.tokenUpdate.message = action.message;
});

registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
